// ============================================================
// useWitnessAutoSave.ts
// Per-witness debounced auto-save.
//
// Unlike useAutoSave (which manages a single Case with one debounce
// timer), witnesses live in a list — each row has its own pending
// timer and its own accumulated partial. A keystroke in witness A
// shouldn't affect witness B's debounce window.
//
// Public API:
//   scheduleWitnessSave(id, changes)  — debounce 1s, accumulate
//                                       changes across keystrokes,
//                                       fire one PATCH when idle.
//   flushWitnessAutoSave()            — fire EVERY pending timer
//                                       immediately. Call before
//                                       navigating away.
//   cancelWitnessSave(id)             — drop the pending timer and
//                                       payload for one witness.
//                                       Used on delete so we don't
//                                       PATCH a row we just removed.
// ============================================================
import { updateWitnessApi, listWitnessesApi } from '../api/casevoApi'
import { Witness } from '../types/case.types'
import {
  getWitnessBaseUpdatedAt, setWitnessBaseUpdatedAt,
  getWitnessBaseValues, setWitnessBaseValues,
} from './saveBaseVersion'
import { serverChangedTouchedColumns, MAX_409_REAPPLY_ATTEMPTS } from './reconcile409'
import { useCasevoStore } from '../store/useCasevoStore'

// Locate the caseId whose witness list contains this row, so a 409 re-fetch can
// call listWitnessesApi(caseId) (there's no witness-by-id endpoint). The edited
// row belongs to the open case; scanning all loaded lists keeps this tolerant
// of a late save after a navigation. Null → can't re-fetch (fall back to toast).
const findWitnessCaseId = (witnessId: string): string | null => {
  const map = useCasevoStore.getState().witnesses
  for (const [caseId, list] of Object.entries(map)) {
    if (list.some((w) => w.id === witnessId)) return caseId
  }
  return null
}

const DEBOUNCE_MS = 1000

// Collab (Yjs) columns on a witness: a passive HTML MIRROR whose merge is owned
// by Yjs (doc `witness:<id>:statement`). A 409 caused solely by such a column
// must NEVER toast — the next snapshot carries the merged HTML. Mirrors
// CASE_COLLAB_COLUMNS in useAutoSave. Only `statement` is collab-owned (Stage 3);
// name/phone/email/address stay on the normal conflict path.
const WITNESS_COLLAB_COLUMNS = new Set<string>(['statement'])

// Module-scope state. Same singleton pattern as useAutoSave but
// keyed by witnessId so each row debounces independently.
const timers = new Map<string, ReturnType<typeof setTimeout>>()
// Accumulated partial — every keystroke merges into here. When the
// timer fires we PATCH with whatever has built up, then clear.
const pendingPayloads = new Map<string, Partial<Witness>>()
// Rows whose last save FAILED (non-409) — this hook owns that save error.
const satelliteErrored = new Set<string>()

const runWitnessSave = async (
  witnessId: string,
  // Column-aware 409 recovery attempt counter (0 = first try). A silent
  // re-apply increments it; capped by MAX_409_REAPPLY_ATTEMPTS.
  attempt: number = 0,
): Promise<void> => {
  const payload = pendingPayloads.get(witnessId)
  if (!payload || Object.keys(payload).length === 0) return
  // Move the payload into a local before the await — if more
  // keystrokes land during the request they'll start a new pending
  // entry rather than getting clobbered by this run's PATCH return.
  pendingPayloads.delete(witnessId)
  try {
    // Optimistic-concurrency: attach the SERVER updatedAt captured at load so
    // the backend can 409 on a stale write. Opt-in — omit if no base captured.
    const base = getWitnessBaseUpdatedAt(witnessId)
    const body = base !== null ? { ...payload, baseUpdatedAt: base } : payload
    const updated = await updateWitnessApi(witnessId, body)
    // Advance the baseline to the server's new updatedAt.
    setWitnessBaseUpdatedAt(witnessId, updated.updatedAt)
    // Advance the column-aware base VALUES to the just-saved server row so the
    // NEXT 409's overlap check compares against what's now on the server.
    setWitnessBaseValues(witnessId, updated as unknown as Record<string, unknown>)
    // Clear a save error THIS hook raised on an earlier failed attempt.
    if (satelliteErrored.delete(witnessId) && satelliteErrored.size === 0) useCasevoStore.getState().setSaveError(null)
  } catch (err) {
    const resp = (err as { response?: { status?: number; data?: { currentUpdatedAt?: string } } }).response
    if (resp?.status === 409) {
      // COLUMN-AWARE recovery (mirrors useAutoSave): the backend guard is
      // timestamp-based, so this may be a SPURIOUS 409 — another user edited
      // DIFFERENT columns of this row. Re-fetch (via the case's witness list —
      // there's no by-id endpoint) and, if the server didn't change any of the
      // columns THIS save touched (vs our base values), silently re-apply on the
      // fresh base — no toast. Only a genuine same-column clash (or exhausted
      // retries / failed re-fetch) surfaces the conflict.
      const touched = Object.keys(payload)
      // Collab (Yjs) columns never drive a conflict — Yjs owns their merge. Base
      // both the overlap decision and the toast on the NON-collab columns only;
      // a 409 whose touched columns are ALL collab resolves silently.
      const nonCollabTouched = touched.filter((c) => !WITNESS_COLLAB_COLUMNS.has(c))
      const baseValues = getWitnessBaseValues(witnessId)
      if (attempt < MAX_409_REAPPLY_ATTEMPTS && touched.length > 0 && baseValues) {
        const caseId = findWitnessCaseId(witnessId)
        if (caseId) {
          try {
            const list = await listWitnessesApi(caseId)
            const server = list.find((w) => w.id === witnessId)
            if (server) {
              const overlap = serverChangedTouchedColumns(
                nonCollabTouched, baseValues, server as unknown as Record<string, unknown>, payload as Record<string, unknown>,
              )
              if (!overlap) {
                // Non-overlap: advance to the fresh server row (updatedAt + base
                // values now including the other user's change) and re-send ONLY
                // our columns. Re-stage the drained payload, MERGING any newer
                // keystroke on top so we don't lose it.
                setWitnessBaseUpdatedAt(witnessId, server.updatedAt)
                setWitnessBaseValues(witnessId, server as unknown as Record<string, unknown>)
                const newer = pendingPayloads.get(witnessId) ?? {}
                pendingPayloads.set(witnessId, { ...payload, ...newer })
                await runWitnessSave(witnessId, attempt + 1)
                return
              }
            }
          } catch { /* re-fetch failed — fall through to the conflict path */ }
        }
      }
      // Genuine same-column conflict, retry cap hit, or re-fetch failed: the
      // existing non-destructive behaviour — advance base from the 409 body (a
      // later keystroke re-saves cleanly), do NOT resubmit, warn via the toast.
      if (resp.data?.currentUpdatedAt) setWitnessBaseUpdatedAt(witnessId, resp.data.currentUpdatedAt)
      // Only a genuine NON-collab conflict warns. If the touched columns were ALL
      // collab (Yjs) fields (e.g. just the statement mirror), resolve silently —
      // Yjs is the content source of truth and the next snapshot carries the
      // merged HTML.
      if (nonCollabTouched.length === 0) return
      useCasevoStore.getState().setSaveConflict(
        "A witness was updated by someone else. Your latest change wasn't saved — please review and re-enter it.",
      )
      return
    }
    // eslint-disable-next-line no-console
    console.error('[WitnessAutoSave] Failed to save witness:', witnessId, err)
    // ★ Don't DROP the change (it was already removed from pending before the
    // request): re-queue it UNDER any newer edit so the next flush/keystroke
    // re-sends it, surface the save error, and retry once for transient
    // (network / 5xx) failures. 4xx = the server rejected the value — no
    // auto-retry loop; the error stays until the user edits it.
    const status = (err as { response?: { status?: number } }).response?.status
    if (status === 404) {
      // Row no longer exists (deleted elsewhere) — nothing to save; drop it
      // quietly rather than leaving a permanent error + leave-page prompt.
      if (satelliteErrored.delete(witnessId) && satelliteErrored.size === 0) useCasevoStore.getState().setSaveError(null)
      return
    }
    useCasevoStore.getState().setSaveError(err instanceof Error ? err : new Error('Save failed'))
    satelliteErrored.add(witnessId)
    if (status !== undefined && status >= 400 && status < 500) {
      // The server REJECTED the value — re-sending the same payload can't
      // succeed, so don't re-queue it (it would pin the leave-page prompt).
      // The error stays until this row next saves successfully.
      return
    }
    // Transient (network / 5xx): re-queue under any newer edit so a flush or
    // "Try again" re-sends it, and auto-retry once.
    pendingPayloads.set(witnessId, { ...payload, ...(pendingPayloads.get(witnessId) ?? {}) })
    if (attempt === 0) {
      setTimeout(() => { void runWitnessSave(witnessId, 1) }, 5000)
    }
  }
}

// ── Stage 1c: apply a REMOTE witness edit without a save-loop ─────────
// Counterpart to applyRemoteCaseEdit, but MUCH simpler: this hook has NO
// whole-row baseline and does NOT subscribe to the store — a save fires ONLY
// when a component calls scheduleWitnessSave from an onChange. So applying a
// remote edit by writing the store row directly (updateWitness) triggers no
// save at all — no loop, no baseline to advance in lockstep. We only:
//   (1) merge the remote field(s) into the specific ROW in the store, and
//   (2) advance THAT row's optimistic-concurrency base to the server updatedAt
//       the sender's save produced (from the message), so this client's next
//       save of that row won't 409 against the remote write.
// Reads the row by id across the per-case witnesses map; no-ops if the row
// isn't loaded (e.g. a late message after the row was deleted).
export const applyRemoteWitnessEdit = (
  witnessId: string,
  updates: Partial<Witness>,
  serverUpdatedAt: string,
): void => {
  const store = useCasevoStore.getState()
  let row: Witness | undefined
  for (const list of Object.values(store.witnesses)) {
    const found = list.find((w) => w.id === witnessId)
    if (found) { row = found; break }
  }
  if (!row) return
  const merged = { ...row, ...updates }
  store.updateWitness(merged)
  setWitnessBaseUpdatedAt(witnessId, serverUpdatedAt)
  // Keep the column-aware base VALUES aligned with the applied remote state so
  // the next local save's 409 overlap check measures against what the server
  // now holds (parallel to useAutoSave advancing lastSavedFlat on apply).
  setWitnessBaseValues(witnessId, merged as unknown as Record<string, unknown>)
}

export const scheduleWitnessSave = (
  witnessId: string,
  changes: Partial<Witness>,
): void => {
  // Merge with any partial that's already queued — fast typing
  // produces several `{ name: '...' }` calls and we want them all
  // collapsed into one PATCH.
  const existing = pendingPayloads.get(witnessId) ?? {}
  pendingPayloads.set(witnessId, { ...existing, ...changes })
  // Reset the debounce window.
  const existingTimer = timers.get(witnessId)
  if (existingTimer !== undefined) clearTimeout(existingTimer)
  const newTimer = setTimeout(() => {
    timers.delete(witnessId)
    void runWitnessSave(witnessId)
  }, DEBOUNCE_MS)
  timers.set(witnessId, newTimer)
}

// Cancel pending timer + payload for one witness. Used on delete so
// we don't PATCH a row that's about to vanish from the backend.
export const cancelWitnessSave = (witnessId: string): void => {
  if (satelliteErrored.delete(witnessId) && satelliteErrored.size === 0) useCasevoStore.getState().setSaveError(null)
  const timer = timers.get(witnessId)
  if (timer !== undefined) {
    clearTimeout(timer)
    timers.delete(witnessId)
  }
  pendingPayloads.delete(witnessId)
}

// (D12 live updates) Is any witness edit still waiting to be saved? A live
// re-read of the list waits until there's none (it would show the server's
// copy over the user's typing).
export const hasPendingWitnessSaves = (): boolean => timers.size > 0 || pendingPayloads.size > 0

// Fire every pending witness save immediately and await them all.
// Mirrors flushAutoSave for the case-wide debounce.
export const flushWitnessAutoSave = async (): Promise<void> => {
  // Include re-queued payloads (a failed save waiting for retry) — not just
  // rows with a live debounce timer — so navigation / "Try again" sends them.
  const ids = Array.from(new Set([...Array.from(timers.keys()), ...Array.from(pendingPayloads.keys())]))
  for (const id of ids) {
    const timer = timers.get(id)
    if (timer !== undefined) clearTimeout(timer)
    timers.delete(id)
  }
  await Promise.all(ids.map((id) => runWitnessSave(id)))
}

// beforeunload guard — mirrors the one in useAutoSave so a refresh
// or tab close mid-typing still prompts the user when there are
// witness edits in the debounce window. Attached once when the
// module is first imported; the early return makes it a no-op when
// nothing's pending. Modern browsers ignore custom messages, so
// preventDefault + returnValue = '' is enough to surface the dialog.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (event: BeforeUnloadEvent) => {
    if (timers.size === 0 && pendingPayloads.size === 0) return
    event.preventDefault()
    event.returnValue = ''
  })
}
